import { ReviewCorrectionScope } from '../../../api/structs';

/**
 * How far a lesson reaches, in the operator's words. The keys are the store's
 * enum verbatim — this file is the ONE place the four values are spelled for a
 * human, so the correction dialog's select and the Lessons list can never drift
 * into two different vocabularies for the same row.
 */
export const REVIEW_SCOPE_LABELS: Record<ReviewCorrectionScope, string> = {
  this_checkpoint: 'This checkpoint',
  this_pipeline: 'This pipeline',
  this_model: 'This model',
  all_pipelines: 'All my pipelines',
};

/** Narrowest reach first — the order the select offers them in. */
export const REVIEW_SCOPE_OPTIONS: ReviewCorrectionScope[] = [
  'this_checkpoint',
  'this_pipeline',
  'this_model',
  'all_pipelines',
];

/**
 * The select's label for one scope. `this_model` names the model it would bind
 * the rule to: "This model" alone never says WHICH model is about to be taught
 * something in every pipeline that uses it.
 */
export const reviewScopeOptionLabel = (scope: ReviewCorrectionScope, modelId: string): string =>
  scope === 'this_model' && modelId
    ? `${REVIEW_SCOPE_LABELS.this_model} (${modelId})`
    : REVIEW_SCOPE_LABELS[scope];

/**
 * The scopes the select may offer. `this_model` needs a model to bind to, so it
 * appears only when the corrected checkpoint's generator names one — or when it
 * is ALREADY the chosen scope, since the distiller may propose it (the backend
 * derives the id itself) and dropping the option would blank the control on a
 * value the judge chose.
 */
export const reviewScopeOptionsFor = (
  modelId: string,
  scope: ReviewCorrectionScope
): ReviewCorrectionScope[] =>
  REVIEW_SCOPE_OPTIONS.filter(
    (option) => option !== 'this_model' || Boolean(modelId) || scope === 'this_model'
  );

/**
 * The label for a stored scope. A model lesson is chipped with the model it was
 * recorded against — the reach of the rule IS that model, in every pipeline. A
 * value this build does not know (a row written by a newer backend) is shown
 * VERBATIM rather than silently relabelled as something narrower than it is.
 */
export const reviewScopeLabel = (scope: string, modelId?: string): string => {
  if (scope === 'this_model' && modelId) return `model · ${modelId}`;
  return (
    REVIEW_SCOPE_LABELS[scope as ReviewCorrectionScope] || scope || REVIEW_SCOPE_LABELS.this_pipeline
  );
};
